import React from 'react';
import { Helmet } from 'react-helmet-async';

const sections = [
  {
    title: 'Zero-tolerance policy',
    body: 'Zodiacus strictly prohibits child sexual abuse and exploitation (CSAE) and child sexual abuse material (CSAM). Prohibited conduct includes solicitation, grooming, sexualization of minors, trafficking, and any content or activity that exploits or endangers children.',
  },
  {
    title: 'User reporting',
    body: 'Users can report users or content through the reporting and safety controls available in Zodiacus. Child-safety and abuse-related concerns may also be sent to the public safety contact below.',
  },
  {
    title: 'Blocking and safety controls',
    body: 'Users can block other users. Zodiacus provides reporting, blocking, and moderation controls intended to reduce harmful interactions and prevent prohibited contact.',
  },
  {
    title: 'Enforcement',
    body: 'Zodiacus may remove content, restrict functionality, suspend accounts, or permanently remove accounts that violate these standards.',
  },
  {
    title: 'Escalation and legal compliance',
    body: 'Where required by applicable law, Zodiacus may preserve relevant records and cooperate with competent authorities or legally designated reporting bodies.',
  },
];

const prohibited = [
  'Child sexual abuse material (CSAM)',
  'Sexual exploitation of minors',
  'Grooming or solicitation of minors',
  'Sexualized content involving minors',
  'Trafficking or facilitation of child exploitation',
  'Attempts to obtain or distribute exploitative material',
  'Threats or coercion involving minors',
];

const secciones = [
  {
    title: 'Política de tolerancia cero',
    body: 'Zodiacus prohíbe estrictamente el abuso y la explotación sexual infantil (CSAE) y el material de abuso sexual infantil (CSAM). Las conductas prohibidas incluyen la captación o solicitud, el grooming, la sexualización de menores, la trata y cualquier contenido o actividad que explote o ponga en peligro a niños, niñas o adolescentes.',
  },
  {
    title: 'Denuncias de usuarios',
    body: 'Los usuarios pueden denunciar usuarios o contenido mediante los controles de denuncia y seguridad disponibles en Zodiacus. Las inquietudes relacionadas con seguridad infantil o abuso también pueden enviarse al contacto público indicado abajo.',
  },
  {
    title: 'Bloqueo y controles de seguridad',
    body: 'Los usuarios pueden bloquear a otros usuarios. Zodiacus ofrece controles de denuncia, bloqueo y moderación destinados a reducir interacciones dañinas y prevenir contactos prohibidos.',
  },
  {
    title: 'Aplicación de las normas',
    body: 'Zodiacus puede eliminar contenido, restringir funciones, suspender cuentas o eliminar permanentemente cuentas que infrinjan estas normas.',
  },
  {
    title: 'Escalamiento y cumplimiento legal',
    body: 'Cuando lo exija la legislación aplicable, Zodiacus podrá preservar registros pertinentes y cooperar con autoridades competentes u organismos de denuncia designados legalmente.',
  },
];

const prohibido = [
  'Material de abuso sexual infantil (CSAM)',
  'Explotación sexual de menores',
  'Grooming, captación o solicitud de menores',
  'Contenido sexualizado que involucre a menores',
  'Trata o facilitación de la explotación infantil',
  'Intentos de obtener o distribuir material de explotación',
  'Amenazas o coacción que involucren a menores',
];

function PolicySection({ title, body }) {
  return (
    <section className="child-safety-section">
      <h2>{title}</h2>
      <p>{body}</p>
    </section>
  );
}

export default function ChildSafetyStandards() {
  return (
    <main className="child-safety-page">
      <Helmet>
        <title>Zodiacus Child Safety Standards</title>
        <meta name="description" content="Zodiacus standards prohibiting child sexual abuse and exploitation, with reporting, blocking, enforcement, and safety contact information." />
        <link rel="canonical" href="https://zodiacus.app/child-safety" />
      </Helmet>

      <style>{`
        .child-safety-page { min-height: 100dvh; box-sizing: border-box; padding: 28px 18px 56px; color: #f8f4ff; background: radial-gradient(circle at 50% 0%, rgba(218,165,32,.15), transparent 34%), linear-gradient(180deg, #250843 0%, #12051f 100%); font-family: Poppins, system-ui, -apple-system, Segoe UI, sans-serif; }
        .child-safety-card { width: min(820px, 100%); box-sizing: border-box; margin: 0 auto; padding: clamp(22px, 5vw, 44px); border: 1px solid rgba(218,165,32,.42); border-radius: 20px; background: rgba(27,8,48,.94); box-shadow: 0 18px 60px rgba(0,0,0,.3); }
        .child-safety-page h1 { margin: 0 0 10px; color: #ffd86b; font-size: clamp(1.8rem, 6vw, 2.75rem); line-height: 1.15; }
        .child-safety-page h2 { margin: 26px 0 8px; color: #ffe69a; font-size: clamp(1.15rem, 3vw, 1.35rem); }
        .child-safety-page p, .child-safety-page li { color: #f4effb; font-size: 1rem; line-height: 1.7; }
        .child-safety-page ul { padding-left: 22px; }
        .child-safety-eyebrow { margin: 0 0 8px; color: #d8c4ef; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; }
        .child-safety-language { margin-top: 42px; padding-top: 34px; border-top: 1px solid rgba(218,165,32,.35); }
        .child-safety-contact { padding: 16px; border-radius: 14px; background: rgba(218,165,32,.1); }
        .child-safety-page a { color: #ffd86b; text-underline-offset: 3px; overflow-wrap: anywhere; }
        .child-safety-links { display: flex; flex-wrap: wrap; gap: 10px 22px; }
        .child-safety-updated { margin-top: 34px; color: #cbbbdc !important; font-size: .9rem !important; }
        @media (max-width: 480px) { .child-safety-page { padding: 14px 10px 36px; } .child-safety-card { border-radius: 14px; } }
      `}</style>

      <article className="child-safety-card">
        <p className="child-safety-eyebrow">Zodiacus safety and compliance</p>
        <h1>Zodiacus Child Safety Standards</h1>
        <p>These standards apply to content and interactions on Zodiacus. Protecting children from sexual abuse, exploitation, and endangerment is a fundamental requirement of our community.</p>

        {sections.map((section) => <PolicySection key={section.title} {...section} />)}

        <section className="child-safety-section">
          <h2>Examples of prohibited conduct</h2>
          <ul>{prohibited.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>

        <section className="child-safety-section child-safety-contact">
          <h2>Child-safety contact</h2>
          <p>For child-safety or abuse-related concerns, contact <a href="mailto:zodiacus.app@gmail.com">zodiacus.app@gmail.com</a>.</p>
        </section>

        <section className="child-safety-section">
          <h2>Related resources</h2>
          <div className="child-safety-links">
            <a href="https://zodiacus.app/privacidad">Privacy Policy</a>
            <a href="https://zodiacus.app/delete-account">Account Deletion</a>
          </div>
        </section>

        <div className="child-safety-language" lang="es-419">
          <p className="child-safety-eyebrow">Seguridad y cumplimiento de Zodiacus</p>
          <h1>Normas de Seguridad Infantil de Zodiacus</h1>
          <p>Estas normas se aplican al contenido y a las interacciones en Zodiacus. Proteger a niños, niñas y adolescentes del abuso sexual, la explotación y las situaciones de peligro es un requisito fundamental de nuestra comunidad.</p>

          {secciones.map((section) => <PolicySection key={section.title} {...section} />)}

          <section className="child-safety-section">
            <h2>Ejemplos de conductas prohibidas</h2>
            <ul>{prohibido.map((item) => <li key={item}>{item}</li>)}</ul>
          </section>

          <section className="child-safety-section child-safety-contact">
            <h2>Contacto de seguridad infantil</h2>
            <p>Para inquietudes relacionadas con seguridad infantil o abuso, escribí a <a href="mailto:zodiacus.app@gmail.com">zodiacus.app@gmail.com</a>.</p>
          </section>

          <section className="child-safety-section">
            <h2>Recursos relacionados</h2>
            <div className="child-safety-links">
              <a href="https://zodiacus.app/privacidad">Política de Privacidad</a>
              <a href="https://zodiacus.app/delete-account">Eliminación de cuenta</a>
            </div>
          </section>
        </div>

        <p className="child-safety-updated">Last updated: August 2026</p>
      </article>
    </main>
  );
}
